import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const logoUrl = "https://customer-assets.emergentagent.com/job_luxury-facial-pro/artifacts/gjd36s6k_Gemini_Generated_Image_rqozrirqozrirqoz.png";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
  { name: "Wedding", path: "/wedding" },
  { name: "Courses", path: "/courses" },
  { name: "Special Courses", path: "/special-courses" },
  { name: "Skills Academy", path: "/skills-academy" },
  { name: "Alumni", path: "/alumni" },
  { name: "Events", path: "/events" },
  { name: "Gift Cards", path: "/gift-cards" },
  { name: "Contact", path: "/contact" },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      <header
        data-testid="header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-[#030303]/95 backdrop-blur-sm border-b border-[#C5A059]/15 shadow-lg shadow-black/30"
            : "bg-gradient-to-b from-[#030303]/80 to-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link
              to="/"
              data-testid="logo-link"
              className="flex items-center gap-3 relative"
            >
              <div className="relative">
                <div className="absolute -inset-2 bg-[#C5A059]/10 rounded-full blur-md" />
                <img 
                  src={logoUrl} 
                  alt="LuxuryBeauty" 
                  className="h-16 w-auto relative drop-shadow-[0_0_8px_rgba(197,160,89,0.4)]"
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`font-body text-[11px] tracking-[0.15em] uppercase transition-colors duration-300 link-underline whitespace-nowrap ${
                    location.pathname === link.path
                      ? "text-[#C5A059]"
                      : "text-white/80 hover:text-white"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* CTA Button - Desktop */}
            <Link
              to="/intake-form"
              data-testid="header-cta"
              className="hidden lg:block ml-6 bg-[#C5A059] text-[#050505] font-semibold text-xs tracking-[0.15em] uppercase px-6 py-3 hover:brightness-110 transition-all duration-300 whitespace-nowrap"
            >
              Register Now
            </Link>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-white p-2"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mobile-menu flex flex-col items-center justify-center"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col items-center gap-8">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={link.path}
                    data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                    className={`font-heading text-3xl tracking-wider transition-colors ${
                      location.pathname === link.path
                        ? "text-[#C5A059]"
                        : "text-white hover:text-[#C5A059]"
                    }`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Link
                  to="/intake-form"
                  data-testid="mobile-cta"
                  className="mt-4 bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-[0.15em] uppercase px-8 py-4"
                >
                  Register Now
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
